import React from 'react';
import { Phone, CheckCircle, Wrench, Shield, Car, Settings, Gauge, AlertTriangle } from 'lucide-react';
export function BasicMaintenancePage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-1770">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-1771">
        <div className="container mx-auto px-4" data-id="element-1772">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-1773">
            Basic Car and Truck Maintenance
          </h1>
          <p className="text-xl text-gray-100 text-center max-w-4xl mx-auto" data-id="element-1774">
            Garrett Auto and Truck Repair has put together a vehicle maintenance
            guide for car and truck owners. This guide is meant to help you keep
            your vehicle running properly and at top efficiency. It does not
            cover all aspects of vehicle maintenance, and it doesn't replace
            your vehicle's OEM maintenance schedule. You should look to this
            guide as the most basic maintenance tips and continue to have your
            vehicle(s) routine inspections and maintenance performed by trained
            and certified mechanics.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-1775">
        <div className="max-w-4xl mx-auto" data-id="element-1776">
          {/* Maintenance Guide Header */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1777">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1778">
              Garrett Auto and Truck Service's Basic Vehicle Maintenance Guide
            </h2>

            {/* Tires and Wheels */}
            <div className="mb-8" data-id="element-1779">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1780">
                Tires and Wheels
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-1781">
                Rotate your tires every 5000 miles. This is not easy if you
                haven't done it and don't have the proper tools and equipment to
                do it safely. There are safety concerns as any car or truck can
                severely injure or kill you if falls on you. Bring your vehicle
                to Garrett Auto and Truck Service if you wish to have your tires
                rotated for you.
              </p>
            </div>

            {/* Tire Pressure Check */}
            <div className="mb-8" data-id="element-1782">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1783">
                Tire Pressure Check
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-1784">
                Anyone who owns a car or truck needs to own a tire pressure
                gauge. These are very common, you can get them at Walmart, gas
                stations and truck stops, and some grocery stores even have
                them. If you have more than one vehicle, buy one for each car
                and leave it in the vehicle. Use it to check each tire for
                proper air pressure. If the air pressure is too low, you can
                have problems and your fuel mileage will suffer. If you are
                uncomfortable with checking your air pressure and filling it to
                the proper PSI, you need to take it to an auto center where it
                can be done for you. Garrett Auto performs this service.
              </p>
            </div>

            {/* Tire Tread Depth Check */}
            <div className="mb-8" data-id="element-1785">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1786">
                Tire Tread Depth Check
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-1787">
                Tires need the proper amount of minimum tread to function
                properly. If your tire tread is too worn, the tire won't
                evacuate road debris such as water, snow, slush and sleet, and
                may lose traction, causing loss of control. Tires where not
                enough tread is left are commonly said to be "bald", since
                they're so much smoother after tread wears them down. Most tires
                today have "wear bars" built in to them. If the wear bars are
                visible, the tire tread is worn to it's limit. New tires are
                required. If you are unfamiliar with finding and recognizing
                tread wear bars on your tires, you can take your vehicle in to
                an auto or truck service and have a pro check them for you. If
                you're lucky enough to be in our area, bring your car or truck
                to Garrett Auto and Truck Service and we'll be happy to inspect
                your tire tread wear.
              </p>
            </div>

            {/* Clean Your Tires and Wheels */}
            <div className="mb-8" data-id="element-1788">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1789">
                Clean Your Tires and Wheels
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-1790">
                Brake dust comes from your brake pads tightening up against your
                brake rotor. The friction causes tiny bits and pieces of the
                rotor and pad to flake off, forming a powder. It's nasty stuff
                and if your leave it on the wheels too long it can get really
                difficult to clean off. The brake dust will become saturated
                with moisture and grime from the road, and then heat from
                applying your brakes will bake it on to your wheels. Brake dust
                often clings to wheels because of static electricity and the
                simple way to remove it is using damp sponge with clean cold
                water.
              </p>
            </div>
          </div>

          {/* Engine Maintenance Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1791">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-1792">
              <Wrench className="text-[#B1050B] mr-3" size={28} data-id="element-1793" />
              Engine Maintenance
            </h2>

            {/* Belt Inspections */}
            <div className="mb-8" data-id="element-1794">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1795">
                Belt Inspections and Replacement
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-1796">
                Your engine is the main power supply for your vehicle. It not
                only makes the car move forward and backward, it also powers the
                components and accessories that make the engine itself operate
                properly and keep the cabin (where you ride) comfortable. How
                does it power those components and accessories? The engine
                turns, and a rubber reinforced belt transfer the rotation from
                the engine to the components and accessories. After the engine
                is started, the engine powers the alternator which keeps the
                battery charged and provides electricity to everything that
                requires electric current from the vehicles on-board computer to
                the lights in the cabin and the radio. In short, healthy belts
                are mandatory for operation of the vehicle. Make sure you have
                the belts inspected at least every 25,000 miles and we recommend
                replacement every 50,000 miles.
              </p>
            </div>

            {/* Oil Level Check */}
            <div className="mb-8" data-id="element-1797">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1798">
                Oil Level Check
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-1799">
                Maintain the proper oil level for optimum engine performance and
                longevity. Most people can check the oil level in their vehicle
                engine. Make sure the vehicle is parked on level ground (not
                down hill or up hill, or tilted down hill sideways) and make
                sure the engine and oil have cooled down so that you don't burn
                yourself. Giving the vehicle some time to cool down also allows
                oil to flow back to the oil pan, which will give you a more
                accurate reading. Go get a clean rag. There's a dipstick under
                the hood, open the hood of your car or truck and find the oil
                level dipstick and pull it out. Wipe the oil off the end that
                was in the engine the furthest and look for oil level range
                indication markings. There is a low and high marking or there's
                a shaded area marked on the tip of the dipstick (end that goes
                into the oil tub or dipstick tube first). Slowly replace the
                dipstick into the tube and slide it all the way down so that the
                end you're holding reseats, closing the oil tube completely. Now
                slowly pull it back out and examine the end with the oil level
                indication markings. Is the oil level within the low and high
                markings or shaded area? If it's below the low mark, you need to
                add a little oil.
              </p>
            </div>

            {/* Engine Oil Replacement */}
            <div className="mb-8" data-id="element-1800">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1801">
                Engine Oil Replacement (Time for an oil change)
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-1802">
                Manufacturers recommend different intervals for oil changes.
                Generally, and oil change every 3,000 miles is safe. The best
                service recommendation is the one your vehicle manufacturer
                recommends for your vehicle and engine. When you have your oil
                changed make sure your oil filter is changed as well. This is
                also a good time to have the air filter changed. Clean filters
                allow the vehicle engine to run more efficiently and last
                longer. Old dirty filters become clogged with debris and road
                grime, causing flow restrictions that may impede proper
                operation of the engine. Worse, dirty filters can allow that
                debris to find it's way past the filters and into your engine
                and components, which can cause expensive damage to your engine
                and fuel system.
              </p>
            </div>

            {/* Engine Coolant Level Check */}
            <div className="mb-8" data-id="element-1803">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1804">
                Engine Coolant Level Check
              </h3>
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4" data-id="element-1805">
                <div className="flex items-center" data-id="element-1806">
                  <AlertTriangle className="text-yellow-600 mr-2" size={20} data-id="element-1807" />
                  <span className="font-bold text-yellow-800" data-id="element-1808">NOTE:</span>
                </div>
                <p className="text-yellow-800 mt-2" data-id="element-1809">
                  If you plan on checking your coolant levels, make certain the
                  vehicle engine and coolant have had enough time to cool down.
                  DO NOT REMOVE Radiator cap from the radiator when the engine
                  and coolant are still hot from the engine running.
                </p>
              </div>
              <p className="text-gray-700 leading-relaxed" data-id="element-1810">
                Coolant provides essential functions for your vehicle engine. It
                keeps the engine from over-heating. Coolant also must not freeze
                during the winter months, so make sure the coolant mixture is
                appropriate for your climate. Coolant usually comes in
                concentrate form and you mix it with water. Make sure to read
                your vehicle manufacturer's recommendations for coolant and
                follow these recommendations. Your manufacturer guide will
                explain where to find your coolant reservoir as well as provide
                basic instructions for check coolant level and maintaining it.
                If you don't want to deal with hot coolant or have no
                inclination to perform vehicle maintenance, take your vehicle to
                a trusted, certified auto mechanic in your area and have the
                coolant levels checked and filled by professionals.
              </p>
            </div>

            {/* Dash Lights/Warning Lights */}
            <div className="mb-4" data-id="element-1811">
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1812">
                Dash Lights/Warning Lights
              </h3>
              <p className="text-gray-700 leading-relaxed" data-id="element-1813">
                If you see any warning lights indicating there may be a problem
                with your vehicle, we recommend you bring it in for diagnosis.
                Our certified professional mechanics will diagnose any problems
                and help you get back on the road. If you're not lucky enough to
                be in our area, (Farmington Hills, MI, bordering Novi and easy
                to get to from Northville and the Detroit Metropolitan area),
                then we highly recommend you find trusted certified professional
                mechanics in your area.
              </p>
            </div>
          </div>

          {/* Call to Action */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8 text-center" data-id="element-1814">
            <a href="tel:2484788382" className="text-[#B1050B] text-2xl font-bold hover:underline" data-id="element-1815">
              (248) 478-8382 (Click to Call)
            </a>

            <h3 className="text-xl font-bold text-[#B1050B] mt-6 mb-4" data-id="element-1816">
              Garrett Auto And Truck Repair Service
            </h3>

            <ul className="text-left space-y-4 max-w-2xl mx-auto" data-id="element-1817">
              <li className="flex items-start" data-id="element-1818">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1819" />
                <span className="text-gray-700" data-id="element-1820">
                  <strong data-id="element-1821">Save time and money:</strong> The "cheaper" mechanic
                  down the road doesn't stand behind their repairs, parts and
                  labor like we do.{' '}
                  <strong data-id="element-1822">
                    We set the industry standard in repair work warranty!
                  </strong>
                </span>
              </li>
              <li className="flex items-start" data-id="element-1823">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1824" />
                <span className="text-gray-700" data-id="element-1825">
                  <strong data-id="element-1826">Accurate Diagnosis:</strong> You'll find taking your
                  car, truck or commercial vehicle anywhere else can end up
                  costing you a lot more money in extra trips back and forth,
                  lost earnings, vehicle down time and still paying extra for
                  "new repairs" that should have been diagnosed and done right
                  the first time.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1827">
                <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-1" size={20} data-id="element-1828" />
                <span className="text-gray-700" data-id="element-1829">
                  We're conveniently located to serve Farmington, Farmington
                  Hills, Novi, Commerce, Walled Lake, Livonia and surrounding
                  areas.
                </span>
              </li>
            </ul>
          </div>

          {/* About Garrett Auto */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1830">
            <h2 className="text-2xl font-bold text-[#B1050B] mb-4" data-id="element-1831">
              Garrett Auto and Truck Repair Serving Southeast Michigan
            </h2>

            <div className="md:flex md:gap-6" data-id="element-1832">
              <div className="md:flex-1" data-id="element-1833">
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1834">
                  Garrett Auto & Truck Service was founded in 1958 as a Standard
                  Oil gas station at the corner of Halstead Road and Grand River
                  Avenue. Doing business under the name of "Grand Halstead
                  Service" we did mechanical repair, towing and road service on
                  anything that had wheels! We continued at that location until
                  September of 1978 when we changed the name and moved into our
                  present facility.
                </p>

                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1835">
                  A great deal has changed in the years since we began serving
                  the repair and maintenance needs of our friends and neighbors
                  here in Farmington Hills. In 1971, Ten Mile was a dirt road!
                  From decades past, working on farm equipment and trucks for
                  local farmers, to present day providing fleet maintenance and
                  repair solutions to corporate clients both large and small at
                  Garrett Auto & Truck, we really do have all the answers for
                  your fleet maintenance and repair needs.
                </p>

                <p className="text-gray-700 leading-relaxed" data-id="element-1836">
                  Our full-time mechanics are licensed, certified in their
                  specialties, and have over 50 years collective experience in
                  maintaining and repairing all types of vehicles. We're experts
                  in our field, capable and qualified to handle everything from
                  tire repair and{' '}
                  <a href="/auto-repair/brake-repair" className="text-[#B1050B] hover:underline font-semibold" data-id="element-1837">
                    brake repair
                  </a>{' '}
                  to complete{' '}
                  <a href="/auto-repair-mechanic.html" className="text-[#B1050B] hover:underline font-semibold" data-id="element-1838">
                    Auto
                  </a>{' '}
                  and Truck engine overhauls.
                </p>
              </div>
              <div className="md:w-64 mt-4 md:mt-0 flex-shrink-0" data-id="element-1839">
                <img src="//drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/uploaded/images/NewDesign/after_overview_text_before_.jpg?opt=w3" alt="Farmington Hills Auto Repair" className="w-full h-auto rounded-lg" data-id="element-1840" />
              </div>
            </div>
          </div>

          {/* Vehicle Mechanics Services */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1841">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1842">
              Car, SUV, Light Truck, Heavy Truck, Commercial Vehicle Mechanics
            </h2>

            <div className="grid md:grid-cols-2 gap-3" data-id="element-1843">
              <div className="flex items-center" data-id="element-1844">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1845" />
                <span className="text-gray-700" data-id="element-1846">Auto Engine Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1847">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1848" />
                <span className="text-gray-700" data-id="element-1849">
                  Automatic Transmission Repair
                </span>
              </div>
              <div className="flex items-center" data-id="element-1850">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1851" />
                <span className="text-gray-700" data-id="element-1852">
                  Manual Transmission Repair
                </span>
              </div>
              <div className="flex items-center" data-id="element-1853">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1854" />
                <span className="text-gray-700" data-id="element-1855">
                  Clutch Inspection and Replacement
                </span>
              </div>
              <div className="flex items-center" data-id="element-1856">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1857" />
                <span className="text-gray-700" data-id="element-1858">Transfer Case Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1859">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1860" />
                <span className="text-gray-700" data-id="element-1861">Transaxle Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1862">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1863" />
                <span className="text-gray-700" data-id="element-1864">Brake System Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1865">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1866" />
                <span className="text-gray-700" data-id="element-1867">U-Joint Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1868">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1869" />
                <span className="text-gray-700" data-id="element-1870">Driveshaft Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1871">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1872" />
                <span className="text-gray-700" data-id="element-1873">Halfshaft Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1874">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1875" />
                <span className="text-gray-700" data-id="element-1876">Steering Column Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1877">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1878" />
                <span className="text-gray-700" data-id="element-1879">Steering System Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1880">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1881" />
                <span className="text-gray-700" data-id="element-1882">Differential Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1883">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1884" />
                <span className="text-gray-700" data-id="element-1885">Rear Axle Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1886">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1887" />
                <span className="text-gray-700" data-id="element-1888">Front Wheel Drive Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1889">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1890" />
                <span className="text-gray-700" data-id="element-1891">Four Wheel Drive Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1892">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1893" />
                <span className="text-gray-700" data-id="element-1894">All Wheel Drive Repair</span>
              </div>
              <div className="flex items-center" data-id="element-1895">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0" size={18} data-id="element-1896" />
                <span className="text-gray-700" data-id="element-1897">Suspension System Repair</span>
              </div>
            </div>
          </div>

          {/* Click to Call */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8 text-center" data-id="element-1898">
            <a href="tel:2484788382" className="text-[#B1050B] text-2xl font-bold hover:underline" data-id="element-1899">
              Click to Call (248) 478-8382
            </a>
          </div>

          {/* Mission Statement */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1900">
            <h2 className="text-2xl font-bold text-[#B1050B] mb-4" data-id="element-1901">
              Garrett Auto and Truck Repair Mission Statement
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1902">
              Garrett Auto & Truck Repair Service is here to serve you by
              performing the highest quality automotive & truck repairs at fair
              prices, using only proven materials of the highest quality.
            </p>

            <p className="text-gray-700 leading-relaxed" data-id="element-1903">
              Our service is backed by our personal pledge of customer
              satisfaction. We will provide you, our customer, with outstanding
              professional courtesy and service. And we will stand behind our
              workmanship.
            </p>
          </div>

          {/* Auto Repair Services */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-1904">
            <h2 className="text-2xl font-bold text-[#B1050B] mb-4" data-id="element-1905">
              Auto Repair, Truck Repair and Maintenance Services
            </h2>

            <p className="text-gray-700 leading-relaxed mb-4" data-id="element-1906">
              <strong data-id="element-1907">Garrett Auto & Truck Repair Service</strong> believes in
              preventative maintenance. Bring your vehicle in for routine oil
              checks and get your oil changed. New, clean oil will lubricate the
              engine appropriately and help protect it from unnecessary wear and
              tear. Oil filter replacement, air filter replacement, and other
              routine maintenance services will keep your vehicle running longer
              and better. We'll help you save time and money by providing the
              diagnostic, repair and replacement services recommended by your
              vehicle manufacturer.
            </p>

            <p className="text-gray-700 leading-relaxed" data-id="element-1908">
              We've put together a great guide for basic automotive and truck
              maintenance that you can refer to if you wish to keep your vehicle
              running right. These are things you can easily do if you have the
              time and want to put in the effort.
            </p>
          </div>

          {/* Images */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-8" data-id="element-1909">
            <div className="flex flex-wrap justify-center gap-4" data-id="element-1910">
              <img src="//drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/uploaded/images/NewDesign/Dump_Truck_Bodies1.jpg?opt=w3" alt="Engine Repair" className="h-48 w-auto rounded-lg" data-id="element-1911" />
              <img src="//drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/uploaded/images/NewDesign/img2.jpg?opt=w3" alt="Auto Repair and Truck Repair" className="h-48 w-auto rounded-lg" data-id="element-1912" />
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-1913">
            <h2 className="text-3xl font-bold mb-4" data-id="element-1914">
              Need Vehicle Maintenance Services?
            </h2>
            <p className="text-xl mb-6" data-id="element-1915">
              Trust our certified mechanics for all your automotive maintenance
              needs
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-1916">
              <Phone className="mr-3" size={24} data-id="element-1917" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-1918">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
            <p className="mt-2 text-gray-400 text-sm" data-id="element-1919">
              Serving the community since 1958
            </p>
          </div>
        </div>
      </div>
    </div>;
}